import "@/components/files/documents.css";
import {
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
} from "react";
import Modal from "@/components/modal";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import FolderTree from "@/components/files/folderTree";
import { IFileItem } from "@/types/FileItem";
import { useTranslation } from "react-i18next";
import FileUploadInput from "@/components/forms/inputs/upload";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/components/state/store";
import { setFileListPaginationMode } from "@/components/state/fileList/fileListSlice";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import styles from "@/components/files/fileList.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import useFileAction from "@/helper/customHooks/useFileActions";
import { Box, Tooltip } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { api } from "@/helper/api";
// import styles from "@/components/files/fileTable.module.css";
import CircularProgress from "@mui/material/CircularProgress";
import { ButtonColor } from "@/components/forms/inputs/button/genericButton";
import { formatFilesize } from "@/helper/formattingValues";
import { replaceDate } from "@/helper/dateHandling";
import {
  formatFilenameString,
  previewFile,
} from "@/components/files/fileUtils";
import {
  buildSimpleMrtColumnFilterApiParams,
  hasIncompleteMrtDateFilter,
} from "@/components/tables/genericCrudTableDateFilterIntegration";
import { normalizeCustomFilterColumns } from "@/components/tables/genericCrudTableFilterColumnIntegration";
import MrtToolbarInternalActions from "@/components/tables/mrtToolbarInternalActions";
import {
  hasMrtFiltersToReset,
  resetMrtFilters,
} from "@/components/tables/mrtTableReset.utils";
//ICONS
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import FileTableIcon from "@/components/files/fileTableIcon";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DownloadIcon from "@mui/icons-material/Download";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FileTableRowActionMenuItem } from "@/components/files/fileTableRowActionMenuItem";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { Upload } from "@mui/icons-material";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

interface Props {
  subjectId?: string;
  folderId?: string;
  folderName?: string;
  onClickHandler?: (id: string) => void;
  query?: string;
  sortByName?: any;
  sortByDate?: any;
  folderUpdate?: any;
  addedSinceLastLogin?: boolean;
}

export default function FileList(props: Props) {
  const { t } = useTranslation();
  const [modalPresetValues, setModalPresetValues] = useState<IFileItem>();
  const [modalValue, setModalValue] = useState<any>([]);
  const [modalUploadOpen, setModalUploadOpen] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [moveTargetError, setMoveTargetError] = useState(false);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, "moveTarget">({
      fieldOrder: [],
    });
  const { handleError, errorModal } = useErrorModal();
  // _____down new states from old filetable

  // InfiniteScroll
  const [isPageAtTop, setIsPageAtTop] = useState(false);
  const [usePagination, setUsePagination] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [paginationModeToggled, setPaginationModeToggled] = useState(false);
  const [showLoadingMoreSpinner, setShowLoadingMoreSpinner] = useState(false);
  const [loadMoreDataTriggerEl, setLoadMoreDataTriggerEl] =
    useState<HTMLElement | null>(null);
  // relevant TablesStates
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [rowCount, setRowCount] = useState(0);
  const [fileData, setFileData] = useState<IFileItem[]>([]);
  const [fileListReady, setFileListReady] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [isRefetching, setIsRefetching] = useState(false);
  const [columnFilter, setColumnFilter] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const preservePaginationOnFilterResetRef = useRef(false);
  //componenent/page related states
  const [, setInitialLoading] = useState<boolean>(false);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [disableDeleteAllButton, setDisableDeleteAllButton] =
    useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [markAllAsSeenCount, setMarkAllAsSeenCount] = useState<number | string>(
    0
  );

  const { showConfirmationModalAsync, showConfirmationModal } =
    useConfirmationModal();

  // material ui
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // get current portal
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];
  // infinite scroll related
  const rootOutletContentContainer =
    document.getElementById("rootOutletContent");
  // default columVisibilities & sorting
  const columnSorting = "-created_on";
  const columnVisibility = {
    id: false,
    name: true,
    size: false,
    visible_to_subject: false,
    locked: false,
    created_on: false,
    extension: false,
    modified_on: false,
    created_by: false,
    modified_by: false,
  };
  const dispatch = useDispatch();

  // ReduxState
  const children: any = useSelector((state: RootState) => state.parent);
  const selectedChildCompany: any = useSelector(
    (state: RootState) => state.parent
  );
  const selectedSubject: any = useSelector(
    (state: RootState) => state.subjectSearch
  );
  const logedInUserInfo: any = useSelector((state: RootState) => state.login);
  const usePaginationFromRedux = useSelector(
    (state: RootState) => state.fileList?.usePagination ?? false
  );

  const isPortalUpload =
    subdomain === "portal" && props.folderName?.toLowerCase() === "upload";
  const isAdmin = subdomain === "admin";
  const isManageAllowed = subdomain === "manage" && props.folderId;

  const handleFilesMarkedAsDownloaded = (
    updatedFiles: Array<{ id: string; has_been_downloaded: true }>
  ) => {
    setFileData((prev: IFileItem[]) =>
      prev.map((file) => {
        const updated = updatedFiles.find(
          (updatedFile) => String(updatedFile.id) === String(file.id)
        );
        return updated ? { ...file, ...updated } : file;
      })
    );
  };

  //customHook imports
  const {
    performFileAction,
    uploadSuccess,
    updateUploadSuccess,
    moveFileSource,
    moveFileTarget,
    updateMoveFileSource,
    updateMoveFileTarget,
    moveFile,
    toggleModalMoveOpen,
    modalMoveOpen,
    editFileName,
    modalEditOpen,
    toggleModalEditOpen,
    deleteFile,
    handleDownloadFile,
    markFileAsDownloaded,
    markAllFilesAsDownloaded,
  } = useFileAction(
    handleError,
    selectedChildCompany?.subjectId,
    selectedSubject?.value
  );

  //ColumnDefinitions--------------------------------------------------------------------------------
  //baseColumns - always visible (all Portals)
  //adminColumns for Portal === "admin"
  const baseColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "id",
        header: t("id"),
        id: "id",
        enableHiding: subdomain !== "admin" ? false : true,
        visibleInShowHideMenu: subdomain !== "admin" ? false : true,
      },

      {
        header: t("name"),
        accessorKey: "name",
        id: "name",
        enableColumnFilter: false,
        size: isMobile ? window.innerWidth * 0.8 : 400,
        Cell: ({ row, renderedCellValue }: any) => {
          return (
            <Box className={styles.fileTableName}>
              <FileTableIcon extension={row.getValue("extension")} />
              <a
                href={previewFile(row, children?.subjectId)}
                target="_blank"
                rel="noreferrer"
                className="fileTableNameLink"
                onClick={() => handleMarkFileAsDownloaded(row.getValue("id"))}
              >
                <Tooltip title={formatFilenameString(renderedCellValue)}>
                  <span
                    className={
                      !row.original.has_been_downloaded
                        ? styles.undownloadedFileBold
                        : undefined
                    }
                  >
                    {formatFilenameString(renderedCellValue)}
                  </span>
                </Tooltip>
              </a>
            </Box>
          );
        },
      },
      {
        header: t("folder"),
        id: "folder",
        enableColumnFiltering: false,
        enableColumnFilter: false,

        accessorFn: (row: any) => {
          const folder = row.folder;
          if (folder) {
            return `${folder.name} `;
          }
        },
      },
      {
        header: t("filesize"),
        size: 80,
        enableColumnFiltering: false,
        id: "size",
        accessorKey: "size",
        enableColumnFilter: false,
        Cell: ({ row }: any) => {
          return formatFilesize(row.original.size);
        },
      },
      ...(subdomain === "admin"
        ? [
            {
              header: t("status"),
              size: 80,
              id: "visible_to_subject, locked",
              Cell: ({ row }: any) => {
                return (
                  <div className={styles.statusIcons}>
                    {row.getValue("visible_to_subject") ? (
                      <Tooltip title={t("visible")}>
                        <VisibilityIcon color="success" />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("not_visible")}>
                        <VisibilityOffIcon color="error" />
                      </Tooltip>
                    )}

                    {row.getValue("locked") ? (
                      <Tooltip title={t("locked")}>
                        <LockIcon color="error" />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("not_locked")}>
                        <LockOpenIcon color="success" />
                      </Tooltip>
                    )}
                  </div>
                );
              },
            },
          ]
        : []),
      {
        header: t("created_on"),
        accessorKey: "created_on",
        id: "created_on",
        size: 80,
        filterVariant: "date",
        sortingFn: "datetime",
        Cell: ({ cell }: any) => {
          return (
            <span>
              {subdomain !== "admin"
                ? replaceDate(cell.getValue())
                : replaceDate(cell.getValue(), true)}
            </span>
          );
        },
      },
      {
        header: t("extension"),
        accessorKey: "extension",
        id: "extension",
      },
      {
        header: t("visible"),
        enableHiding: false,
        Header: <VisibilityIcon />,
        accessorKey: "visible_to_subject",
        visibleInShowHideMenu: false,

        id: "visible_to_subject",
        Cell: ({ row }: any) => {
          return (
            <>
              {row.getValue("visible_to_subject") ? (
                <VisibilityIcon color="success" />
              ) : (
                <VisibilityOffIcon color="error" />
              )}
            </>
          );
        },
      },
      {
        header: t("locked"),
        enableHiding: false,
        Header: <LockIcon />,
        accessorKey: "locked",
        visibleInShowHideMenu: false,

        id: "locked",
        Cell: ({ row }: any) => {
          return (
            <>
              {row.getValue("locked") ? (
                <LockIcon color="error" />
              ) : (
                <LockOpenIcon color="success" />
              )}
            </>
          );
        },
      },
    ],
    []
  );
  const adminColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: t("modified_on"),
        accessorKey: "modified_on",
        id: "modified_on",
        filterVariant: "date",
        sortingFn: "datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue())}</span>;
        },
      },
      {
        header: t("created_by"),
        accessorKey: "created_by",
        customColumnFilterId: "created_by_name",

        id: "created_by",
        Cell: ({ cell }: any) => {
          return <span>{cell.getValue()?.name}</span>;
        },
      },
      {
        header: t("modified_by"),
        accessorKey: "modified_by",
        customColumnFilterId: "modified_by_name",
        id: "modified_by",
        Cell: ({ cell }: any) => {
          return <span>{cell.getValue()?.name}</span>;
        },
      },
    ],
    []
  );
  //Setting columns for table - checking subdomain for baseColumns or adding adminColumns--------------------------------------------------------------------------------
  const columns = useMemo(
    () =>
      normalizeCustomFilterColumns(
        subdomain === "admin" ? [...baseColumns, ...adminColumns] : baseColumns
      ),
    [baseColumns, adminColumns, subdomain]
  );

  //defining TableOptions for setting them in one Place (easier adding and maintaining them) and accessing them within table.
  const tableOptions = {
    filters: true,
    columnHiding:
      subdomain === "admin" || subdomain === "manage" ? true : false,
  };

  const handleResetTableFilters = (table: MRT_TableInstance<IFileItem>) => {
    preservePaginationOnFilterResetRef.current = true;
    resetMrtFilters(table);
  };

  //Defining the Table--------------------------------------------------------------------------------
  const table = useMaterialReactTable({
    data: fileData,
    columns,
    localization:
      logedInUserInfo.preferred_language === "en"
        ? MRT_Localization_EN
        : MRT_Localization_DE,
    enableHiding: tableOptions.columnHiding,
    enableFullScreenToggle: false,
    enableFilters: tableOptions.filters,
    positionToolbarAlertBanner: "none",
    enableDensityToggle: isMobile ? false : true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionGlobalFilter: "left",
    enableColumnFilters: subdomain === "admin" ? true : false,
    manualPagination: true,
    rowCount: rowCount,
    enableRowSelection: true,
    enableGlobalFilter: false,
    manualFiltering: true,
    enableRowActions: true,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        visibleInShowHideMenu: false,
        size: 40,
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            padding: "0.5rem",
          },
        },
        muiTableBodyCellProps: {
          sx: {
            padding: "0.5rem",
            textAlign: "center",
            borderRight: "1px solid #e0e0e0",
            "& .MuiIconButton-root": {
              margin: 0,
            },
          },
        },
      },
      "mrt-row-select": {
        visibleInShowHideMenu: false,
        size: 40,
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            padding: "0.5rem",
          },
        },
        muiTableBodyCellProps: {
          sx: {
            padding: "0.5rem",
            textAlign: "center",
            borderRight: "1px solid #e0e0e0",
            "& .MuiIconButton-root": {
              margin: 0,
            },
          },
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(100vh - 240px)",
      },
    },
    muiTablePaperProps: {
      ...({
        "data-testid": "file-list-results",
        "data-ready": fileListReady,
        "aria-busy": !fileListReady,
      } as Record<string, string | boolean>),
      sx: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginTop: isMobile ? "0" : "1rem",

        "&& .MuiCollapse-root, && .MuiCollapse-wrapperInner": {
          width: "100%",
        },
        "& .MuiAlert-root": {
          backgroundColor: "#fff",
        },
        "& .MuiInputBase-root, && .MuiInput-root::before, && .MuiInput-root::after":
          {
            borderBottom: "none",
          },
        "& .MuiTextField-root": {
          backgroundColor: "#f4f2f2",
          outline: "none",
          width: "100%",
          "& .MuiOutlinedInput-root": {
            fontSize: "1.25rem",
          },
          "& .MuiInput-root": {
            paddingLeft: "0.5rem",
            paddingRight: "0.5rem",
          },
        },
        "&& .MuiBox-root": {
          alignItems: "center",
        },
        "& .MuiTablePagination-root": {
          display: usePagination ? undefined : "none",
        },
        "& .MuiTableHead-root": { opacity: 1 },
      },
    },
    muiTopToolbarProps: {
      sx: isMobile
        ? {
            "&& .MuiBox-root": {
              display: "block",
            },
          }
        : {},
    },
    muiTableBodyRowProps: ({ row }: any) => {
      const isLastRow = row.index === fileData.length - 1;
      return {
        className: styles.tableRow,
        "data-testid": `file-list-row-${row.original.id}`,
        ref: !usePagination && isLastRow ? setLoadMoreDataTriggerEl : null,
        sx: {
          backgroundColor: row.index % 2 === 0 ? "#f5f5f5" : "#FFF",
        },
      };
    },
    muiTableBodyCellProps: {
      sx: {
        borderRight: "1px solid #e0e0e0",
      },
    },

    initialState: {
      columnVisibility: columnVisibility,
      density: "comfortable",
      showGlobalFilter: false,
      showColumnFilters: subdomain === "admin" ? true : false,
    },
    state: {
      pagination: pagination,
      columnFilters: columnFilter,
      showProgressBars: isRefetching,
      sorting: sorting,
      rowSelection: rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    onPaginationChange: (e: any) => {
      setPagination(e);
      setInitialLoading(false);
    },
    onColumnFiltersChange: (e: any) => {
      handleColumnFilter(e);
    },
    onSortingChange: (e) => {
      setSorting(e);
      setInitialLoading(false);
    },
    renderToolbarInternalActions: ({ table }) => (
      <MrtToolbarInternalActions
        table={table}
        showReset={hasMrtFiltersToReset(table.getState())}
        onReset={() => handleResetTableFilters(table)}
      />
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) =>
      rowActionMenuDefinition.map((action: any) => (
        <FileTableRowActionMenuItem
          key={action.key}
          onClick={() => action.onClick(row, closeMenu)}
          disabled={action.disabled(row)}
          icon={action.icon(row)}
          label={action.label(row)}
        />
      )),
    renderBottomToolbar: () => {
      if (!usePagination) {
        return (
          <Box className={styles.bottomToolbarWrapper}>
            <div data-testid="file-list-bottom-toolbar">
              <span className={styles.numOfFilesDisplayed}>
                {t("number_of_files_displayed")}:&nbsp;
              </span>
              {fileData.length} {t("out_of")} {rowCount}
            </div>
            <div className={styles.spinnerContainer}>
              <CircularProgress
                size={20}
                thickness={5}
                color="secondary"
                className={`
                      ${styles.loadingMoreSpinner}
                      ${
                        showLoadingMoreSpinner
                          ? styles.loadingMoreSpinnerVisible
                          : styles.loadingMoreSpinnerHidden
                      }
                    `}
              />
            </div>
          </Box>
        );
      }
      return null;
    },
    renderTopToolbarCustomActions: ({ table }) => {
      let toolBarItems: ReactNode = <></>;
      const selectedFlatRows = table.getSelectedRowModel().flatRows;
      const hasSelectedRows = selectedFlatRows.length > 0;

      const submitMultiple = async (
        submitMethod: any,
        submitEntity: any,
        data?: any,
        deselectRows = true,
        skipQuestion?: false
      ) => {
        const additionalRouteParts: any = {};

        let confirmQuestion = "";
        let result = data;
        let useBulkCall = false;

        if (submitMethod === "download") {
          setIsDownloading(true);
          showConfirmationModal(t("download_in_progress"));
          const selectedRows = table.getSelectedRowModel().flatRows;
          const rows = selectedRows.map((file) => file.original);

          await handleDownloadFile(rows, handleFilesMarkedAsDownloaded);
          setIsDownloading(false);
          return;
        }

        if (submitMethod === "edit") {
          for (const file of table.getSelectedRowModel().flatRows) {
            const row = file.original;
            handleOpenEditModal(row);
          }
          return;
        }

        if (submitMethod === "delete") {
          confirmQuestion = t("selected_files_delete");
        }

        if (submitMethod === "put") {
          for (const key in data) {
            if (key === "visible_to_subject") {
              if (data[key] === true) {
                confirmQuestion = t("selected_files_set_visible");
              } else confirmQuestion = t("selected_files_set_invisible");
            }
            if (key === "locked") {
              if (data[key] === true) {
                confirmQuestion = t("selected_files_lock_all");
              } else confirmQuestion = t("selected_files_unlock_all");
            }
            if (key === "mark_downloaded") {
              if (data[key] === "selected") {
                additionalRouteParts["mark_downloaded"] = "/";
                useBulkCall = true;
                const selectedRows = table.getSelectedRowModel().flatRows;
                const rows = selectedRows.map((file) => file.original.id);

                result = { file_set: rows };
                confirmQuestion = t("mark_as_read_selected_confirm");
              }
            }
          }
        }

        if (!skipQuestion) {
          const isConfirmed = await showConfirmationModalAsync(confirmQuestion);
          if (!isConfirmed) return;
        }

        try {
          if (useBulkCall) {
            await new Promise((resolve) => {
              api.genericApiRequest({
                method: submitMethod,
                entity: submitEntity,
                additionalRouteParts: additionalRouteParts,
                submitData: result,

                successHandler: (res: any) => {
                  if (submitMethod === "put") {
                    setFileData((prevData) => {
                      const updatedFiles = res.data;
                      const updatedMap = new Map(
                        updatedFiles.map((f: any) => [f.id, f])
                      );

                      return prevData.map((file) => {
                        const updated = updatedMap.get(file.id);
                        return updated ? { ...file, ...updated } : file;
                      });
                    });
                  }
                  if (deselectRows) {
                    setRowSelection({});
                  }

                  resolve(res.data);
                  fetchData();
                },
                failHandler: (error: any) => {
                  console.log(error);
                },
              });
            });
          } else {
            const promises = table.getSelectedRowModel().flatRows.map((row) => {
              return new Promise((resolve) => {
                api.genericApiRequest({
                  method: submitMethod,
                  entity: submitEntity,
                  entityId: row.original.id,
                  additionalRouteParts: additionalRouteParts,
                  submitData: result,

                  successHandler: (res: any) => {
                    if (submitMethod === "put") {
                      setFileData((prevData) => {
                        return prevData.map((file) =>
                          file.id === row.original.id
                            ? {
                                ...file,
                                ...data,
                              }
                            : file
                        );
                      });
                    }

                    if (submitMethod === "delete") {
                      setFileData((prevData) =>
                        prevData.filter((file) => file.id !== row.original.id)
                      );
                    }

                    resolve(res.data);
                  },
                  failHandler: (error: any) => {
                    console.log(error);
                  },
                });
              });
            });
            await Promise.all(promises).then(() => {
              if (deselectRows) {
                setRowSelection({});
              }

              fetchData();
            });
          }
        } catch (error) {
          console.log(error);
        }
      };

      let paginationScrollToggle: ReactNode = <></>;
      if (!isMobile) {
        paginationScrollToggle = (
          <Tooltip
            title={
              usePagination
                ? t("change_to_infinite_scroll")
                : t("change_to_pagination")
            }
            placement="top"
          >
            <GenericButton
              variant="icon"
              color="iconBase"
              onClick={handlePaginationScrollToggle}
            >
              {usePagination ? <AutoStoriesIcon /> : <AllInclusiveIcon />}
            </GenericButton>
          </Tooltip>
        );
      }
      const renderActionButton = (
        title: any,
        icon: any,
        color: ButtonColor,
        action: any
      ) => (
        <Tooltip title={t(title)} placement="top">
          <GenericButton variant="icon" color={color} onClick={action}>
            {icon}
          </GenericButton>
        </Tooltip>
      );
      const renderMobileActionButton = (
        title: any,
        icon: any,
        action: any,
        disabled = false
      ) => (
        <Tooltip
          title={disabled && t("at_least_one_file_must_be_selected")}
          placement="top"
        >
          <span>
            <GenericButton
              startIcon={icon}
              className={styles.mobileActionButton}
              onClick={action}
              disabled={disabled}
            >
              {t(title)}
            </GenericButton>
          </span>
        </Tooltip>
      );

      toolBarItems = (
        <>
          {subdomain === "admin" && (
            <>
              {renderActionButton(
                t("visible_to_subject_all"),
                <VisibilityIcon color="success" />,
                "secondary",
                () =>
                  submitMultiple("put", "file", {
                    visible_to_subject: true,
                  })
              )}
              {renderActionButton(
                t("not_visible_to_subject_all"),
                <VisibilityOffIcon />,
                "error",
                () =>
                  submitMultiple("put", "file", {
                    visible_to_subject: false,
                  })
              )}
              {renderActionButton(
                t("file_unlock_all"),
                <LockOpenIcon />,
                "secondary",
                () => submitMultiple("put", "file", { locked: false }, false)
              )}
              {renderActionButton(
                t("file_lock_all"),
                <LockIcon />,
                "error",
                () => submitMultiple("put", "file", { locked: true })
              )}
              {renderActionButton(
                "move",
                <DriveFileMoveIcon />,
                "primary",
                () => {
                  const selectedRows = table
                    .getSelectedRowModel()
                    .flatRows.map((row) => row.original);
                  openMoveFileModal(selectedRows);
                }
              )}
            </>
          )}
          {/* DownloadButton combining mobile and desktop */}
          <Tooltip title={t("download")} placement="top">
            <GenericButton
              variant={"icon"}
              onClick={() => submitMultiple("download", "file")}
              showSpinnerOnDisable={true}
              disabled={isDownloading}
              id="down"
              waitingForResponse={isDownloading}
              className={
                isMobile && subdomain !== "admin"
                  ? styles.mobileActionButton
                  : ""
              }
            >
              {<DownloadIcon />}
            </GenericButton>
          </Tooltip>
          {/* mark as read selected combining mobile and desktop */}

          <Tooltip title={t("mark_as_read_selected")} placement="top">
            <GenericButton
              variant={"icon"}
              onClick={() =>
                submitMultiple("put", "file", { mark_downloaded: "selected" })
              }
              className={
                isMobile && subdomain !== "admin"
                  ? styles.mobileActionButton
                  : ""
              }
            >
              <DoneIcon />
            </GenericButton>
          </Tooltip>

          {/* Separate container for action buttons on Mobile */}
          <Box className={styles.mobileActionButtonsContainer}>
            {isMobile &&
              renderMobileActionButton(
                t("rename"),
                <EditIcon />,
                () => submitMultiple("edit", "file"),
                (subdomain !== "admin" &&
                  selectedFlatRows.some((row: any) => row.original.locked)) ||
                  !hasSelectedRows ||
                  selectedFlatRows.length > 1
              )}
            {/* DeleteButton */}
            <Tooltip
              placement="top"
              title={
                disableDeleteAllButton &&
                hasSelectedRows &&
                t("file_multidelte_not_all_eligible")
              }
            >
              <span>
                <GenericButton
                  onClick={() => {
                    submitMultiple("delete", "file");
                  }}
                  disabled={disableDeleteAllButton}
                  variant={isMobile ? "contained" : "icon"}
                  startIcon={isMobile && <DeleteIcon />}
                  color={"error"}
                  className={`${
                    isMobile ? styles.mobileActionButton : undefined
                  }`}
                >
                  {!isMobile ? <DeleteIcon /> : t("delete")}
                </GenericButton>
              </span>
            </Tooltip>
          </Box>
        </>
      );

      return (
        <Fragment>
          <div className={styles.toolBarItemsContainer}>
            {hasSelectedRows && toolBarItems}
          </div>
          {!isMobile && (
            <div className={styles.paginationToggleContainer}>
              {paginationScrollToggle}
            </div>
          )}
        </Fragment>
      );
    },
  });

  //Effects & fetch--------------------------------------------------------------------------------
  useEffect(() => {
    const additionalUrlParameters: any = {};
    additionalUrlParameters["has_been_downloaded"] = false;
    if (props.subjectId) {
      additionalUrlParameters["impersonate_subject"] = props.subjectId;
    }
    api.genericApiRequest({
      entity: "file",
      method: "get",
      parametersToRender: {
        depth: "0",
        limit: "1", //because we actually just need the count - 0 does get ignored and returns all
        additionalUrlParameters: additionalUrlParameters,
      },

      successHandler: (res: any) => {
        const countOfUnseenFiles = res.data.count;

        setMarkAllAsSeenCount(countOfUnseenFiles);
      },
      failHandler: (error: any) => {
        console.log(error);
      },
    });
  }, [fileData]);

  const handleGetUnseenFileCount = () => {
    const additionalUrlParameters: any = {};
    additionalUrlParameters["has_been_downloaded"] = false;
    if (props.subjectId) {
      additionalUrlParameters["impersonate_subject"] = props.subjectId;
    }
    api.genericApiRequest({
      entity: "file",
      method: "get",
      parametersToRender: {
        depth: "0",
        limit: "1", //because we actually just need the count - 0 does get ignored and returns all
        additionalUrlParameters: additionalUrlParameters,
      },

      successHandler: (res: any) => {
        const countOfUnseenFiles = res.data.count;

        setMarkAllAsSeenCount(countOfUnseenFiles);
      },
      failHandler: (error: any) => {
        console.log(error);
      },
    });
  };
  useEffect(() => {
    if (
      (!props.folderId && moveFileSource.length !== 0) ||
      (!props.folderId && !uploadSuccess)
    ) {
      setInitialLoading(true);
    } else if (uploadSuccess || props.folderId || props.subjectId) {
      setInitialLoading(false);
    }
  }, [
    props.folderId,
    moveFileSource,
    uploadSuccess,
    props.subjectId,
    columnFilter,
  ]);

  const fetchData = async (pageIndex?: number) => {
    setFileListReady(false);
    if (fileData.length !== 0) {
      setIsRefetching(true);
    }

    const additionalUrlParameters: any = {};
    if (props.folderId && !props.query) {
      additionalUrlParameters["folder_id"] = props.folderId;
    }
    if (props.addedSinceLastLogin) {
      additionalUrlParameters["added_since_last_login"] = true;
      additionalUrlParameters["has_been_downloaded"] = false;
    }
    if (props.subjectId) {
      additionalUrlParameters["impersonate_subject"] = props.subjectId;
    }
    if (columnFilter.length !== 0) {
      if (hasIncompleteMrtDateFilter(columnFilter, columns)) {
        setIsRefetching(false);
        return;
      }
      Object.assign(
        additionalUrlParameters,
        buildSimpleMrtColumnFilterApiParams(columnFilter, columns)
      );
    }
    let pIndex = 0;
    if (pageIndex) {
      pIndex = pageIndex;
    } else pIndex = pagination.pageIndex;
    try {
      /*eslint-disable */
      const res: any = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          entity: "file",
          method: "get",
          parametersToRender: {
            depth: "0",
            ordering: columnSorting,
            limit: pagination.pageSize,
            offset: pIndex * pagination.pageSize,
            search: props.query || "",
            additionalUrlParameters: additionalUrlParameters,
          },

          successHandler: (res: any) => {
            resolve(res);
            /*eslint-enable */
          },
          failHandler: (error: any) => reject(error),
        });
      });
      const data = res.data;

      if (usePagination || pIndex === 0) {
        setFileData(data.results);
      } else {
        setFileData((prevData) => {
          const newData = data.results.filter(
            (newFile: any) =>
              !prevData.some((prevFile: any) => prevFile.id === newFile.id)
          );
          return [...prevData, ...newData];
        });
      }
      setRowCount(data.count);
      setFileListReady(true);
      setInitialLoading(true);
      const files = [];
      for (const key in data.results) {
        const item = data.results[key];
        const id = item.id;
        const name = item.name;
        files.push({ id: id, name: name });
      }

      handleUploadReset();
      handleGetUnseenFileCount();
    } catch (error) {
      handleError(error);

      return;
    } finally {
      setIsRefetching(false);
    }
  };

  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      return;
    }
    if (preservePaginationOnFilterResetRef.current) {
      preservePaginationOnFilterResetRef.current = false;
      return;
    }
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [props.folderId, props.query, props.addedSinceLastLogin, columnFilter]);

  useEffect(() => {
    if (props.query && props.query.length > 0 && props.query.length < 3) {
      setFileData([]);
      setRowCount(0);
      return;
    }

    if (!props.folderId && !props.query && !props.addedSinceLastLogin) {
      setFileData([]);
      setRowCount(0);
      return;
    }

    fetchData(pagination.pageIndex);
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    props.folderId,
    props.query,
    props.addedSinceLastLogin,
    columnFilter,
    props.subjectId,
    uploadSuccess,
  ]);

  const debounce = (func: any, delay: number) => {
    let timer: any;
    return (...args: any) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    const tableContainer = document.querySelector(".MuiTableContainer-root");

    const handleIntersection = debounce(
      (entries: IntersectionObserverEntry[]) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          !isRefetching &&
          fileData.length < rowCount
        ) {
          setShowLoadingMoreSpinner(true);
          handleLoadMoreData();
        }
      },
      300
    );

    const handleScroll = debounce(() => {
      if (!usePagination && !isRefetching && tableContainer) {
        const bottomReached =
          tableContainer.scrollHeight - tableContainer.scrollTop <=
          tableContainer.clientHeight + 100;
        const allDataFetched = fileData.length === rowCount;

        if (bottomReached && !allDataFetched) {
          setShowLoadingMoreSpinner(true);
          handleLoadMoreData();
        } else if (allDataFetched) {
          setShowLoadingMoreSpinner(false);
        }
      }
    }, 300);

    if (loadMoreDataTriggerEl && !usePagination) {
      observer = new IntersectionObserver(handleIntersection, {
        threshold: 0.5,
      });
      observer.observe(loadMoreDataTriggerEl);
    }

    if (isMobile && tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (observer && loadMoreDataTriggerEl) {
        observer.unobserve(loadMoreDataTriggerEl);
        observer.disconnect();
        observer = null;
      }
      if (isMobile && tableContainer) {
        tableContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [
    loadMoreDataTriggerEl,
    isRefetching,
    usePagination,
    isMobile,
    fileData.length,
    rowCount,
  ]);

  useEffect(() => {
    if (!showLoadingMoreSpinner || isRefetching) {
      return;
    }

    const timeout = setTimeout(() => {
      setShowLoadingMoreSpinner(false);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [showLoadingMoreSpinner, isRefetching]);

  useEffect(() => {
    if (isMobile) {
      setUsePagination(false);
    } else {
      setUsePagination(usePaginationFromRedux);
    }
  }, [isMobile, usePaginationFromRedux]);

  useEffect(() => {
    if (paginationModeToggled) {
      setFileData([]);
      setPagination((prevPagination) => ({
        ...prevPagination,
        pageIndex: 0,
      }));
      fetchData(0);
      setPaginationModeToggled(false);
    }
  }, [paginationModeToggled]);

  useEffect(() => {
    const isElementScrollable = (element: any) => {
      return element && element.scrollHeight > element.clientHeight;
    };

    const checkScrollable = () => {
      if (rootOutletContentContainer) {
        if (isElementScrollable(rootOutletContentContainer) && rowCount !== 0) {
          if (!showScrollButton) {
            setShowScrollButton(true);
          }
        } else {
          if (showScrollButton) {
            setTimeout(() => setShowScrollButton(false), 250);
          }
        }
      }
    };

    checkScrollable();
    window.addEventListener("resize", checkScrollable);
    return () => {
      window.removeEventListener("resize", checkScrollable);
    };
  }, [rowCount, showScrollButton]);

  useEffect(() => {
    const handleScroll = () => {
      if (rootOutletContentContainer) {
        const scrollTop = rootOutletContentContainer.scrollTop;
        const isPageAtTop = scrollTop <= 10;
        setIsPageAtTop(isPageAtTop);
      }
    };
    const debouncedHandleScroll = debounce(handleScroll, 100);

    if (rootOutletContentContainer) {
      rootOutletContentContainer.addEventListener(
        "scroll",
        debouncedHandleScroll
      );
    }

    handleScroll();
    return () => {
      if (rootOutletContentContainer) {
        rootOutletContentContainer.removeEventListener(
          "scroll",
          debouncedHandleScroll
        );
      }
    };
  }, []);

  useEffect(() => {
    const data = table
      .getSelectedRowModel()
      .flatRows.map((item: any) => item.original);
    const hasLockedRows = data.some((item: any) => item.locked);
    setDisableDeleteAllButton(hasLockedRows);
  }, [rowSelection, fileData]);

  const handleDeleteFile = (file: any) => {
    deleteFile(file, (deletedFileId) => {
      setFileData((prev) => prev.filter((f) => f.id !== deletedFileId));
      fetchData();
    });
  };
  const rowActionMenuDefinition = [
    {
      key: 0,
      onClick: (row: any, closeMenu: () => void) => {
        handleOpenEditModal(row.original);
        closeMenu();
      },
      disabled: (row: any) => subdomain !== "admin" && row.original.locked,
      icon: () => <EditIcon />,
      label: () => t("rename"),
    },
    {
      key: 1,
      onClick: (row: any, closeMenu: () => void) => {
        // deleteFile(row.original);
        handleDeleteFile(row.original);
        closeMenu();
      },
      disabled: (row: any) => row.original.locked,
      icon: () => <DeleteIcon color="error" />,
      label: () => t("delete"),
    },
    {
      key: 2,
      onClick: (row: any, closeMenu: () => void) => {
        handleDownloadFile(row.original, handleFilesMarkedAsDownloaded);
        closeMenu();
      },
      disabled: () => false,
      icon: () => <DownloadIcon />,
      label: () => t("download"),
    },

    subdomain === "admin"
      ? {
          key: 4,
          onClick: (row: any, closeMenu: () => void) => {
            if (moveFileSource?.length === 0) {
              openMoveFileModal(row.original);
            }
            closeMenu();
          },
          disabled: () => false,
          icon: () => <DriveFileMoveIcon />,
          label: () => t("move"),
        }
      : null,

    subdomain === "admin"
      ? {
          key: 5,
          onClick: (row: any, closeMenu: () => void) => {
            if (row.original.reviewed) {
              handleMarkAsUnreviewed(row.original);
            } else {
              handleMarkAsReviewed(row.original);
            }
            closeMenu();
          },
          disabled: () => false,
          icon: (row: any) =>
            row.original.reviewed ? (
              <CheckCircleOutlineIcon color="error" />
            ) : (
              <CheckCircleOutlineIcon color="success" />
            ),
          label: (row: any) =>
            row.original.reviewed ? t("set_unreviewed") : t("set_reviewed"),
        }
      : null,

    subdomain === "admin"
      ? {
          key: 6,
          onClick: (row: any, closeMenu: () => void) => {
            if (row.original.visible_to_subject) {
              handleMarkAsInvisibleToSubject(row.original);
            } else {
              handelMarkAsVisibleToSubject(row.original);
            }
            closeMenu();
          },
          disabled: () => false,
          icon: (row: any) =>
            row.original.visible_to_subject ? (
              <VisibilityOffIcon color="error" />
            ) : (
              <VisibilityIcon color="success" />
            ),
          label: (row: any) =>
            row.original.visible_to_subject
              ? t("not_visible_to_subject")
              : t("visible_to_subject"),
        }
      : null,

    subdomain === "admin"
      ? {
          key: 7,
          onClick: (row: any, closeMenu: () => void) => {
            if (row.original.locked) {
              handleMarkAsUnlocked(row.original);
            } else {
              handleMarkAsLocked(row.original);
            }
            closeMenu();
          },
          disabled: () => false,
          icon: (row: any) =>
            row.original.locked ? (
              <LockOpenIcon color="success" />
            ) : (
              <LockIcon color="error" />
            ),
          label: (row: any) =>
            row.original.locked ? t("file_locked") : t("file_unlocked"),
        }
      : null,
  ].filter(Boolean);

  //HandleFunctions--------------------------------------------------------------------------------
  const handleUpdateFileData = (updatedFields: any) => {
    setFileData((prev: any[]) =>
      prev.map((file) =>
        file.id === updatedFields.id ? { ...file, ...updatedFields } : file
      )
    );
  };
  const handleUpdateMultiFileData = () => {
    setFileData((prev: any[]) =>
      prev.map((file) => ({
        ...file,
        has_been_downloaded: true,
      }))
    );
  };

  const handleUploadReset = () => {
    updateUploadSuccess(false);
  };

  const handleOpenEditModal = (item: any) => {
    setModalPresetValues(item);
    setModalValue({
      name: item.name,
      extension: item.extension,
    });
    toggleModalEditOpen(true);
  };

  const handleEditModalValueChange = (event: any) => {
    event.preventDefault();
    setModalValue({
      name: event.target.value,
      extension: modalValue.extension,
    });
  };

  const handleMoveFileCancel = () => {
    updateMoveFileSource([]);
    updateMoveFileTarget(null);
    toggleModalMoveOpen(false);
    setMoveTargetError(false);
  };

  const openMoveFileModal = (items: IFileItem | IFileItem[]) => {
    updateMoveFileSource(items);
    updateMoveFileTarget(null);
    toggleModalMoveOpen(true);
    setMoveTargetError(false);
  };

  const handleMarkAsReviewed = (item: any) => {
    performFileAction(item, "markAsReviewed", (updatedFile) => {
      handleUpdateFileData(updatedFile);
      fetchData();
    });
  };
  const handleMarkAsUnreviewed = (item: any) => {
    performFileAction(item, "markAsUnreviewed", (updatedFile) => {
      handleUpdateFileData(updatedFile);
      fetchData();
    });
  };

  const handelMarkAsVisibleToSubject = (item: any) => {
    performFileAction(item, "markAsVisibleToSubject", (updatedFile) => {
      handleUpdateFileData(updatedFile);
      fetchData();
    });
  };

  const handleMarkAsInvisibleToSubject = (item: any) => {
    performFileAction(item, "markAsInvisibleToSubject", (updatedFile) => {
      handleUpdateFileData(updatedFile);
      fetchData();
    });
  };

  const handleMarkAsLocked = (item: any) => {
    performFileAction(item, "markAsLocked", (updatedFile) => {
      handleUpdateFileData(updatedFile);
      fetchData();
    });
  };

  const handleMarkAsUnlocked = (item: any) => {
    performFileAction(item, "markAsUnLocked", (updatedFile) => {
      handleUpdateFileData(updatedFile);
      fetchData();
    });
  };
  const handleColumnFilter = (searchValues: any) => {
    setColumnFilter(searchValues);
  };

  const handleMarkFileAsDownloaded = (id: string) => {
    markFileAsDownloaded(id, subdomain, (updatedFile) => {
      handleUpdateFileData(updatedFile);
      fetchData();
    });
  };
  const handleMarkAllFileAsDownloaded = () => {
    markAllFilesAsDownloaded(() => {
      handleUpdateMultiFileData();
      fetchData();
      // setMarkAllAsSeenCount(false);
    });
  };

  const handleLoadMoreData = () => {
    if (isRefetching || fileData.length >= rowCount) {
      return;
    }
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: prevPagination.pageIndex + 1,
    }));
  };

  const handlePaginationScrollToggle = () => {
    const fileTablePaginationMode = !usePagination;
    setUsePagination((prev) => !prev);
    setPaginationModeToggled(true);
    dispatch(setFileListPaginationMode(fileTablePaginationMode));
  };
  const handleScrollToTop = () => {
    if (rootOutletContentContainer) {
      rootOutletContentContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handleScrollToBottom = () => {
    if (rootOutletContentContainer) {
      rootOutletContentContainer.scrollTo({
        top: rootOutletContentContainer.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleEditFileName = (
    modalValue: any,
    selectedChildCompany: any,
    modalPresetValues: any
  ) => {
    editFileName(
      modalValue,
      selectedChildCompany,
      modalPresetValues,
      (updatedFile) => {
        handleUpdateFileData(updatedFile);
        fetchData();
      }
    );
  };

  const handleMoveFile = async () => {
    if (!moveFileTarget) {
      setMoveTargetError(true);
      focusFirstInvalidField({}, ["moveTarget"]);
      return;
    }

    await moveFile((movedFileId) => {
      setFileData((prev) => prev.filter((f) => f.id !== movedFileId));
    });

    setRowSelection({});

    fetchData();
    props.folderUpdate?.();
  };

  const showUploadButton = isPortalUpload || isAdmin || isManageAllowed;

  return (
    <>
      <div className={styles.topTableButtonWrapper}>
        {subdomain !== "admin" && markAllAsSeenCount !== 0 && (
          <Tooltip title={t("mark_as_read_all")} placement="top">
            <GenericButton
              onClick={handleMarkAllFileAsDownloaded}
              showSpinnerOnDisable={true}
              startIcon={<DoneAllIcon />}
              className={isMobile ? styles.mobileActionButton : ""}
            >
              {t("mark_as_read_all")}
            </GenericButton>
          </Tooltip>
        )}
        {showUploadButton && (
          <>
            <Tooltip title={t("upload")} placement="top">
              <GenericButton
                className={styles.uploadButton}
                onClick={() => {
                  setModalUploadOpen(true);
                }}
                startIcon={<Upload />}
              >
                {t("upload")}
              </GenericButton>
            </Tooltip>

            <Modal
              open={modalUploadOpen}
              onClose={() => setModalUploadOpen(false)}
              title={t("upload_file")}
              hideDialogActions={true}
              disableCancelButton={uploadInProgress}
              maxWidth="40rem"
            >
              <FileUploadInput
                multiple={true}
                subjectId={props.subjectId}
                folderId={props.folderId}
                name="file"
                onUploadStateChange={setUploadInProgress}
                onCancel={() => {
                  setModalUploadOpen(false);
                }}
                handleUploadSuccess={(modalOpen: boolean) => {
                  if (modalOpen) {
                    setModalUploadOpen(false);
                  }
                  updateUploadSuccess(true);
                  props.folderUpdate();
                }}
              />
            </Modal>
          </>
        )}
      </div>
      <Modal
        presetValues={modalPresetValues}
        open={modalEditOpen}
        onClose={() => toggleModalEditOpen(false)}
        submitButtonText={t("save")}
        onSubmit={() =>
          handleEditFileName(
            modalValue,
            selectedChildCompany,
            modalPresetValues
          )
        }
        title={t("edit_filename")}
      >
        <DefaultInput
          value={modalValue.name}
          onChange={handleEditModalValueChange}
        />
      </Modal>
      <Modal
        presetValues={modalPresetValues}
        open={modalMoveOpen}
        onClose={handleMoveFileCancel}
        submitButtonText={t("move")}
        onSubmit={handleMoveFile}
        title={t("choose_folder")}
        disableSubmit={
          Array.isArray(moveFileSource)
            ? moveFileSource.length === 0
            : !moveFileSource
        }
        maxWidth="md"
        contentClassName={styles.moveFolderModalContent}
      >
        <div ref={registerValidationTarget("moveTarget")}>
          <FolderTree
            simpleFolderView={true}
            twoColumnLayout={true}
            source="document"
            onClickHandler={(folderId: string) => {
              updateMoveFileTarget(folderId);
              setMoveTargetError(false);
            }}
            folderId={moveFileTarget ?? ""}
            subjectId={props.subjectId}
          />
          {moveTargetError && <span role="alert">{t("field_required")}</span>}
        </div>
      </Modal>
      <MaterialReactTable table={table} />
      {showScrollButton && (
        <GenericButton
          onClick={isPageAtTop ? handleScrollToBottom : handleScrollToTop}
          className={styles.scrollToTopButton}
        >
          {isPageAtTop ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
        </GenericButton>
      )}
      {errorModal}
    </>
  );
}
